import { toastManager, type ToastActionShortcut } from "@salestrak/ui";

import { isPageHotkey } from "#app/lib/hooks/use-hotkey";
import { IS_MAC } from "#app/lib/platform";

/** Keycaps for the chord that undoes the last delete while its toast stands.
 *  The toast's Undo action wears them and the shortcuts reference derives its
 *  row from them; the binding fires on metaKey OR ctrlKey, so both labels are
 *  true. */
export const UNDO_SHORTCUT: readonly string[] = IS_MAC ? ["⌘", "Z"] : ["Ctrl", "Z"];
export const REDO_SHORTCUT: readonly string[] = IS_MAC
  ? ["⌘", "⇧", "Z"]
  : ["Ctrl", "Shift", "Z"];

/** How long a delete-undo toast stands before the delete is final. */
export const UNDO_TOAST_TIMEOUT_MS = 6000;

/**
 * The chord a delete-undo toast binds to its Undo action. ⌘Z inside a text
 * field is that field's own undo and stays with it, and a modal dialog owns
 * the keys while open — an undo of something no longer on screen is a
 * surprise, not a shortcut. `KeyZ` is the physical key, so the chord fires on
 * every layout.
 */
export const UNDO_TOAST_SHORTCUT: ToastActionShortcut = {
  keys: UNDO_SHORTCUT,
  matches: (event) => isPageHotkey(event, { code: "KeyZ", mod: true }),
};

export function showUndoToast({
  onUndo,
  ...toast
}: {
  title: string;
  description?: string;
  onUndo: () => void;
}): void {
  const toastId = toastManager.add({
    ...toast,
    actionProps: {
      children: "Undo",
      onClick: () => {
        onUndo();
        toastManager.close(toastId);
      },
    },
    data: { actionShortcut: UNDO_TOAST_SHORTCUT },
    timeout: UNDO_TOAST_TIMEOUT_MS,
  });
}
